// WCAG relative luminance + contrast-based text color selection.
//
// Deliberately not a fixed "click count > threshold" rule (which the spec
// explicitly calls out as insufficient - "ne samo fiksnega praga števila
// klikov") and not a naive "luminance > 0.5" rule either: this picks
// whichever of black/white text has the higher WCAG contrast ratio against
// the actual background color, which is the standard accessible approach
// and handles the mid-gradient colors (turquoise/amber) correctly.

import type { Rgb } from "./colorScale";

function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance, in [0, 1]. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const R = srgbToLinear(r);
  const G = srgbToLinear(g);
  const B = srgbToLinear(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/** WCAG contrast ratio between two relative luminances, in [1, 21]. */
export function contrastRatio(lumA: number, lumB: number): number {
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

const BLACK_TEXT = "#0f172a";
const WHITE_TEXT = "#ffffff";

/**
 * Returns the text color (near-black or white) with the higher contrast
 * ratio against the given background.
 */
export function textColorForBackground(background: Rgb): string {
  const bgLuminance = relativeLuminance(background);
  const contrastWithBlack = contrastRatio(bgLuminance, relativeLuminance({ r: 15, g: 23, b: 42 }));
  const contrastWithWhite = contrastRatio(bgLuminance, relativeLuminance({ r: 255, g: 255, b: 255 }));
  return contrastWithBlack >= contrastWithWhite ? BLACK_TEXT : WHITE_TEXT;
}
